import { Plural } from '@lingui/macro';
import classnames from 'classnames';
import React, { FC, memo } from 'react';

import Image from 'common/Image/Image';
import ProviderBadge from 'common/ProviderBadge/ProviderBadge';
import ProviderBadges, { ProfileBadge } from 'common/ProviderBadges/ProviderBadges';
import RatingStars from 'common/RatingStars/RatingStars';
import config from 'config/config';
import routes from 'utils/routeTranslator';

import './ProviderInfoCard.scss';

type ProviderSchemaProps = {
  slug: string;
  publicName: string;
  avatarUrl: string;
  cityName: string;
  regionName: string;
  cityLat: number;
  cityLong: number;
  rating: number;
  feedbacksCount: number;
  ratingEnabled?: boolean;
};

const ProviderSchema: FC<ProviderSchemaProps> = (props) => {
  const profileUrl = `${config.SITE_URL}${routes.get('front::provider.profile', { slug: props.slug })}`;

  const json: any = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': profileUrl,
    'url': profileUrl,
    'name': props.publicName,
    'image': props.avatarUrl,
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': props.cityName,
      'addressRegion': props.regionName,
      'addressCountry': 'PL',
    },
    'areaServed': {
      '@type': 'Place',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': props.cityName,
        'addressRegion': props.regionName,
        'addressCountry': 'PL',
      },
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': props.cityLat,
      'longitude': props.cityLong,
    },
    'priceRange': '0zł',
    'telephone': '-',
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }}></script>;
};

export type ProviderInfoCardProps = {
  slug: string;
  firstName: string;
  lastName: string;
  publicName: string;
  avatarUrl: string;
  cityName?: string;
  regionName?: string;
  cityLat?: number;
  cityLong?: number;
  rating: number;
  feedbacksCount: number;
  featured?: boolean;
  newcomer?: boolean;
  companyName?: string;
  shortDescription?: string;
  badges?: ProfileBadge[];
};

const ProviderInfoCard: FC<ProviderInfoCardProps> = (props) => {
  const { shortDescription, rating, cityName, cityLong, cityLat, regionName } = props;
  const fullName = `${props.firstName} ${props.lastName}`;

  const shortDescriptionCN = classnames('providerInfoCard__shortDescription', {
    providerInfoCard__shortDescription_empty: !shortDescription,
  });

  const avatarWrapperCN = classnames('providerInfoCard__avatarWrapper', {
    providerInfoCard__avatarWrapper_featured: props.featured,
  });

  return (
    <div className="providerInfoCard" id="proCardMessage">
      <div className="providerInfoCard__inner">
        <div className="providerInfoCard__mainInfo">
          <div className={avatarWrapperCN}>
            <Image
              src={props.avatarUrl}
              webp
              multiPixelRatios
              size="s"
              className="providerInfoCard__avatar"
              alt=""
              lazy
            />

            {(props.featured || props.newcomer) && (
              <div className="providerInfoCard__featuredBadgeWrapper">
                <ProviderBadge
                  featured={props.featured}
                  newcomer={props.newcomer}
                  className="providerInfoCard__featuredBadge"
                />
              </div>
            )}
          </div>

          <h3 className="providerInfoCard__fullname">{fullName}</h3>
          <p className="providerInfoCard__address">
            {props.companyName && <strong>{`${props.companyName}, `}</strong>}
            {cityName}
          </p>
        </div>

        <div className="providerInfoCard__badgesWrapper">
          {rating > 0 && (
            <div className="providerInfoCard__ratingWrapper">
              <div className="providerInfoCard__ratingSection">
                <div className="providerInfoCard__ratingWrapper">
                  <RatingStars rating={rating || 0} size="normal" />
                  <span className="providerInfoCard__ratingValue">{rating > 0 ? rating.toFixed(1) : 0}</span>
                  <span className="providerInfoCard__ratingsCount">
                    <Plural value={props.feedbacksCount} one="# opinia" few="# opinie" other="# opinii" />
                  </span>
                </div>
              </div>
            </div>
          )}

          {/*It seems like there are no cases when badges are visible*/}
          {!!props.badges && <ProviderBadges smallMode badges={props.badges} className="providerInfoCard__badges" />}
        </div>

        {shortDescription && (
          <div className="providerInfoCard__descriptionWrapper">
            <p className={shortDescriptionCN}>{shortDescription}</p>
          </div>
        )}

        {cityName && regionName && cityLat && cityLong && (
          <ProviderSchema
            slug={props.slug}
            publicName={props.publicName}
            avatarUrl={props.avatarUrl}
            cityName={cityName}
            regionName={regionName}
            cityLat={cityLat}
            cityLong={cityLong}
            rating={rating}
            feedbacksCount={props.feedbacksCount}
          />
        )}
      </div>
    </div>
  );
};

export default memo(ProviderInfoCard);
